import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Paper
} from '@mui/material';
import {
  Folder,
  CloudUpload,
  CloudDownload,
  Delete,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import '../../css/OutPutDirectory.css';
import { useSelector } from 'react-redux';
import { FORM_KEYS } from '../../utills/constant';

const ReviewScreen = () => {
  const { formDetails, bucketName } = useSelector((state) => state.CreateAgentSlice);
  const [expandedSections, setExpandedSections] = useState({
    input: true,
    bucket: true,
    output: true,
    moved: true,
    rejected: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusIcon = (type) => {
    switch (type) {
      case 'input': return <CloudUpload sx={{ color: '#1976d2' }} />;
      case 'bucket': return <Folder sx={{ color: '#2e7d32' }} />;
      case 'output': return <CloudDownload sx={{ color: '#ed6c02' }} />;
      case 'moved': return <CheckCircle sx={{ color: '#2e7d32' }} />;
      case 'rejected': return <Error sx={{ color: '#d32f2f' }} />;
      default: return <Info sx={{ color: '#1976d2' }} />;
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'input': return '#e3f2fd';
      case 'bucket': return '#e8f5e8';
      case 'output': return '#fff3e0';
      case 'moved': return '#e8f5e8';
      case 'rejected': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  const sections = [
    {
      key: 'input',
      title: 'INPUT DIRECTORY',
      icon: 'input',
      details: [
        { 
          label: 'Directory', 
          value: formDetails[FORM_KEYS.INPUT_DIRECTORY_ID],
          highlight: true
        },
        { 
          label: formDetails[FORM_KEYS.INPUT_DIRECTORY_ID] === 'document-upload' ? 'Uploaded Files' : 'Url / path', 
          value: formDetails[FORM_KEYS.INPUT_DIRECTORY_ID] === 'document-upload' 
            ? `${formDetails[FORM_KEYS.UPLOADED_FILES_METADATA]?.length || 0} files uploaded`
            : formDetails[FORM_KEYS.INPUT_DIRECTORY_PATH_OR_ADDRESS],
          chip: formDetails[FORM_KEYS.INPUT_DIRECTORY_ID] === 'document-upload' ? 'Files' : 'URL'
        }
      ]
    },
    {
      key: 'bucket',
      title: 'BUCKET NAME',
      icon: 'bucket',
      details: [
        { 
          label: 'Name', 
          value: bucketName?.data?.bucket_name,
          highlight: true
        }
      ]
    },
    {
      key: 'output',
      title: 'OUTPUT DIRECTORY',
      icon: 'output',
      details: [
        { 
          label: 'Directory', 
          value: formDetails[FORM_KEYS.OUTPUT_DIRECTORY_ID],
          highlight: true
        },
        { 
          label: "Url's", 
          value: formDetails[FORM_KEYS.OUTPUT_DIRECTORY_PATH_OR_ADDRESS],
          chip: 'URL'
        },
        { 
          label: 'Formats', 
          value: formDetails[FORM_KEYS.OUTPUT_FILE_FORMAT],
          chip: 'Format'
        }
      ]
    },
    {
      key: 'moved',
      title: 'MOVED FILES',
      icon: 'moved',
      details: [
        { 
          label: "Url's", 
          value: formDetails[FORM_KEYS.PROCESSED_FILE_DIRECTORY_PATH_OR_ADDRESS],
          chip: 'URL'
        },
        { 
          label: 'Formats', 
          value: formDetails[FORM_KEYS.OUTPUT_FILE_FORMAT],
          chip: 'Format'
        }
      ]
    },
    {
      key: 'rejected',
      title: 'REJECTED FILES',
      icon: 'rejected',
      details: [
        { 
          label: "Url's", 
          value: formDetails[FORM_KEYS.REJECTED_FILE_DIRECTORY_PATH_OR_ADDRESS],
          chip: 'URL'
        },
        { 
          label: 'Formats', 
          value: formDetails[FORM_KEYS.OUTPUT_FILE_FORMAT],
          chip: 'Format'
        }
      ]
    }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      backgroundColor: '#f8fafc',
      borderRadius: 3,
      p: { xs: 2, md: 3 },
      overflowX: 'hidden',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: '2px solid #e3f2fd',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
          }}>
            <CheckCircle sx={{ color: 'white', fontSize: { xs: 20, md: 24 } }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              color: '#1a202c',
              mb: 0.5,
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}>
              Configuration Review
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#64748b',
              fontSize: { xs: '0.8rem', md: '0.9rem' }
            }}>
              Review your agent configuration before deployment
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`${sections.length} Sections`}
          sx={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', md: '0.85rem' }
          }}
        />
      </Box>

      {/* Sections Grid */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {sections.map((section, index) => (
          <Grid item xs={12} sm={6} lg={4} key={section.key}>
            <Card sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              },
              overflow: 'hidden'
            }}>
              {/* Section Header */}
              <Box sx={{
                background: getStatusColor(section.icon),
                p: { xs: 1.5, md: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                  {getStatusIcon(section.icon)}
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                    color: '#1a202c',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {section.title}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => toggleSection(section.key)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,1)',
                    }
                  }}
                >
                  {expandedSections[section.key] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              {/* Section Content */}
              {expandedSections[section.key] && (
                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {section.details.map((detail, idx) => (
                      <Box key={idx} sx={{
                        p: { xs: 1, md: 1.5 },
                        borderRadius: 2,
                        backgroundColor: detail.highlight ? '#f8fafc' : 'transparent',
                        border: detail.highlight ? '1px solid #e2e8f0' : 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: detail.highlight ? '#f1f5f9' : '#f8fafc',
                        }
                      }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        <Typography sx={{ 
                          color: '#64748b', 
                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                          fontWeight: 600,
                          minWidth: { xs: '60px', md: '80px' },
                          flexShrink: 0
                        }}>
                          {detail.label}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          flex: 1,
                          justifyContent: 'flex-end',
                          minWidth: 0
                        }}>
                          {detail.chip && (
                            <Chip
                              label={detail.chip}
                              size="small"
                              sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                height: { xs: '18px', md: '20px' },
                                flexShrink: 0
                              }}
                            />
                          )}
                          <Typography sx={{ 
                            color: '#1a202c', 
                            fontSize: { xs: '0.75rem', md: '0.8rem' },
                            fontWeight: 500,
                            textAlign: 'right',
                            wordBreak: 'break-word',
                            maxWidth: { xs: '120px', sm: '150px', md: '200px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {detail.value || 'Not specified'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            )}
          </Card>
        </Grid>
      ))}
    </Grid>

      {/* Summary Footer */}
      <Box sx={{
        mt: 4,
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            width: { xs: 36, md: 40 },
            height: { xs: 36, md: 40 },
            borderRadius: '50%',
            backgroundColor: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <CheckCircle sx={{ color: 'white', fontSize: { xs: 18, md: 20 } }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: '#1a202c',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}>
              Configuration Complete
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#64748b',
              fontSize: { xs: '0.8rem', md: '0.85rem' }
            }}>
              All required fields have been configured
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Chip 
            label="Ready to Deploy"
            sx={{
              backgroundColor: '#10b981',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.8rem' }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ReviewScreen;
